<script setup lang="ts">
    import type { components } from '@shopware/api-client/api-types';

    const props = defineProps<{
        media: components['schemas']['Media'];
        type: 'productCard' | 'thumbnail' | 'mainPicture';
        alt?: string;
        class?: string;
        quality?: number;
        eager?: boolean;
        zoom?: boolean;
        zoomClass?: string;
    }>();

    const MAX_IMAGE_WIDTH = 1920;
    const MIN_IMAGE_WIDTH = 80;

    function buildUrl(
        baseUrl: string,
        metaData: { width: number; height: number } | null,
        targetSize: number,
        quality = 90
    ): string {
        const [path, query] = baseUrl.split('?');
        const params = new URLSearchParams(query);

        if (metaData?.width && metaData?.height) {
            const isLandscape = metaData.width >= metaData.height;
            // since out images are squares we should use the short side of the image for generating a size
            if (isLandscape) {
                params.set('height', targetSize.toString());
            } else {
                params.set('width', targetSize.toString());
            }
        } else {
            params.set('width', targetSize.toString());
        }

        params.set('quality', quality.toString());
        return `${path}?${params.toString()}`;
    }

    // target size in px – used to generate widths
    const targetSizeMap: Record<typeof props.type, number> = {
        productCard: 460,
        thumbnail: 80,
        mainPicture: 600
    };

    const sizeMap: Record<typeof props.type, string> = {
        productCard: '(min-width: 1280px) 460px, (min-width: 1020px) 50vw, 100vw',
        thumbnail: '80px',
        mainPicture: '(min-width: 768px) 600px, (min-width: 1280px) 50vw, 98vw'
    };

    const sizes = sizeMap[props.type];
    const base = targetSizeMap[props.type];
    const quality = props.quality ?? 90;
    const metaData = props.media?.metaData as { width: number; height: number } | null;

    // generate multiples for high pixel density displays
    const scaleFactors = [0.5, 0.75, 1, 1.5, 2, 2.5, 3];

    const scaledWidths = scaleFactors.map((factor) => Math.round(base * factor)); // scale base size
    const uniqueWidths = Array.from(new Set(scaledWidths)); // remove duplicates
    const widths = uniqueWidths.filter((width) => width <= MAX_IMAGE_WIDTH && width >= MIN_IMAGE_WIDTH); // clamp width

    const srcset = widths.map((w) => `${buildUrl(props.media.url, metaData, w, quality)} ${w}w`).join(', ');
</script>

<template>
    <ClientOnly v-if="props.zoom">
        <VueImageZoomer
            :click-zoom="true"
            :show-message="false"
            :zoom-amount="3"
            :img-class="zoomClass"
            :regular="buildUrl(props.media.url, metaData, base, quality)"
            :zoom="buildUrl(props.media.url, metaData, base, quality)"
            :regular-webp="buildUrl(props.media.url, metaData, base, quality)"
            :zoom-webp="buildUrl(props.media.url, metaData, base, quality)"
            :alt="props.alt || props.media.alt || ''"
        />

        <template #fallback>
            <img
                :src="buildUrl(props.media.url, metaData, base, quality)"
                :alt="props.alt || props.media.alt || ''"
                :srcset="srcset"
                :sizes="sizes"
                :class="props.class"
                :loading="props.eager ? undefined : 'lazy'"
                :fetchpriority="props.eager ? 'high' : undefined"
                decoding="async"
            />
        </template>
    </ClientOnly>

    <img
        v-else
        :src="buildUrl(props.media.url, metaData, base, quality)"
        :alt="props.alt || props.media.alt || ''"
        :srcset="srcset"
        :sizes="sizes"
        :class="props.class"
        :loading="props.eager ? undefined : 'lazy'"
        :fetchpriority="props.eager ? 'high' : undefined"
        decoding="async"
    />
</template>
